import { useCallback, useEffect, useRef, useState } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SUBSCRIBERS = new Map<string, Set<(arg: any) => void>>();

type SubscriberValue = unknown;

export function scheduleLocalStorageSubscriberBroadcast(
  key: string,
  value: SubscriberValue,
) {
  const schedule =
    typeof queueMicrotask === "function"
      ? queueMicrotask
      : (callback: () => void) => Promise.resolve().then(callback);

  schedule(() => {
    const listeners = SUBSCRIBERS.get(key);
    if (!listeners || listeners.size === 0) {
      return;
    }
    listeners.forEach((listener) => {
      listener(value);
    });
  });
}

export function registerLocalStorageSubscriberForTests(
  key: string,
  listener: (value: SubscriberValue) => void,
) {
  let listeners = SUBSCRIBERS.get(key);
  if (listeners === undefined) {
    listeners = new Set();
    SUBSCRIBERS.set(key, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      SUBSCRIBERS.delete(key);
    }
  };
}

function readLocalStorageValue<T>(key: string, initialValue: T, isBrowser: boolean) {
  if (!isBrowser) {
    return initialValue;
  }
  const item = window.localStorage.getItem(key);
  if (item) {
    try {
      return JSON.parse(item) as T;
    } catch (error) {
      console.warn(
        `Failed to parse localStorage key "${key}", resetting to initial value.`,
        error,
      );
      window.localStorage.removeItem(key);
    }
  }
  return initialValue;
}

export function useLocalStorage<T>(key: string, initialValue: T) {
  const isBrowser = typeof window !== "undefined";
  const initialValueRef = useRef(initialValue);
  const [storedValue, setStoredValue] = useState<T>(() =>
    readLocalStorageValue(key, initialValue, isBrowser),
  );
  const storedValueRef = useRef(storedValue);

  useEffect(() => {
    initialValueRef.current = initialValue;
  }, [initialValue]);

  useEffect(() => {
    storedValueRef.current = storedValue;
  }, [storedValue]);

  useEffect(() => {
    const nextValue = readLocalStorageValue(key, initialValueRef.current, isBrowser);
    storedValueRef.current = nextValue;
    setStoredValue(nextValue);
  }, [isBrowser, key]);

  useEffect(() => {
    if (!isBrowser) {
      return;
    }
    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          const newValue = JSON.parse(e.newValue) as T;
          storedValueRef.current = newValue;
          setStoredValue(newValue);
        } catch (error) {
          console.error("Error parsing storage event value:", error);
        }
      } else if (e.key === key && e.newValue === null) {
        // Key was removed, reset to initial value
        storedValueRef.current = initialValueRef.current;
        setStoredValue(initialValueRef.current);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleValueUpdate = (arg: any) => {
      storedValueRef.current = arg;
      setStoredValue(arg);
    };

    window.addEventListener("storage", handleStorageEvent);
    let listeners = SUBSCRIBERS.get(key);
    if (listeners === undefined) {
      listeners = new Set();
      SUBSCRIBERS.set(key, listeners);
    }
    listeners.add(handleValueUpdate);
    return () => {
      window.removeEventListener("storage", handleStorageEvent);
      listeners.delete(handleValueUpdate);
    };
  }, [isBrowser, key, setStoredValue]);

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      const previousValue = storedValueRef.current;
      const resolvedValue =
        typeof value === "function"
          ? (value as (prev: T) => T)(previousValue)
          : value;
      const shouldPersist = !Object.is(previousValue, resolvedValue);

      try {
        if (!shouldPersist) {
          return;
        }

        storedValueRef.current = resolvedValue;
        setStoredValue(resolvedValue);

        if (typeof window === "undefined") {
          return;
        }

        window.localStorage.setItem(key, JSON.stringify(resolvedValue));
        scheduleLocalStorageSubscriberBroadcast(key, resolvedValue);
      } catch (error) {
        console.error("Error setting localStorage:", error);
      }
    },
    [key],
  );

  return [storedValue, setValue] as const;
}
